/**
 * Authorization Service
 * Handles all authorization-related API calls
 */
import api from './api';

const authorizationService = {
  /**
   * Check if an inspector is authorized for a specific form type + equipment type
   * @param {number} employeeId - Employee ID
   * @param {number} formTypeId - Form Type ID
   * @param {number} equipmentTypeId - Equipment Type ID
   * @returns {Promise} - { authorized: boolean, authorization: object, message: string }
   */
  checkAuthorization: async (employeeId, formTypeId, equipmentTypeId) => {
    try {
      const response = await api.post('/api/authorization/check', {
        employee_id: employeeId,
        form_type_id: formTypeId,
        equipment_type_id: equipmentTypeId
      });
      return response.data;
    } catch (error) {
      console.error('Error checking authorization:', error);
      throw error;
    }
  },

  /**
   * Get all authorizations for a specific inspector
   * @param {number} inspectorId - Inspector ID
   * @param {object} filters - Optional filters { is_active: boolean, form_type_id: number }
   * @returns {Promise} - { count: number, authorizations: array }
   */
  getInspectorAuthorizations: async (inspectorId, filters = {}) => {
    try {
      const params = new URLSearchParams();

      if (filters.is_active !== undefined) {
        params.append('is_active', filters.is_active);
      }

      if (filters.form_type_id) {
        params.append('form_type_id', filters.form_type_id);
      }

      const response = await api.get(`/api/authorization/inspector/${inspectorId}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching inspector authorizations:', error);
      throw error;
    }
  },

  /**
   * Get all inspectors authorized for a specific equipment type
   * @param {number} formTypeId - Form Type ID
   * @param {number} equipmentTypeId - Equipment Type ID
   * @returns {Promise} - { count: number, inspectors: array }
   */
  getAuthorizedInspectors: async (formTypeId, equipmentTypeId) => {
    try {
      const response = await api.get(`/api/authorization/equipment/${formTypeId}/${equipmentTypeId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching authorized inspectors:', error);
      throw error;
    }
  },

  /**
   * Get authorized equipment types for an inspector and form type
   * @param {number} inspectorId - Inspector ID
   * @param {number} formTypeId - Form Type ID
   * @returns {Promise} - Array of authorized equipment types
   */
  getAuthorizedEquipment: async (inspectorId, formTypeId) => {
    try {
      const response = await authorizationService.getInspectorAuthorizations(inspectorId, {
        is_active: true,
        form_type_id: formTypeId
      });

      // Extract unique equipment type IDs
      const authorizedEquipmentIds = response.authorizations.map(auth => auth.equipment_type_id);

      return {
        count: response.count,
        equipment_ids: authorizedEquipmentIds,
        authorizations: response.authorizations
      };
    } catch (error) {
      console.error('Error fetching authorized equipment:', error);
      throw error;
    }
  },

  /**
   * Create a new authorization (admin only)
   * @param {object} authorizationData - Authorization data
   * @returns {Promise}
   */
  createAuthorization: async (authorizationData) => {
    try {
      const response = await api.post('/api/authorization/create', authorizationData);
      return response.data;
    } catch (error) {
      console.error('Error creating authorization:', error);
      throw error;
    }
  },

  /**
   * Update an authorization (admin only)
   * @param {number} authorizationId - Authorization ID
   * @param {object} updateData - Update data
   * @returns {Promise}
   */
  updateAuthorization: async (authorizationId, updateData) => {
    try {
      const response = await api.put(`/api/authorization/${authorizationId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating authorization:', error);
      throw error;
    }
  },

  /**
   * Delete an authorization (admin only)
   * @param {number} authorizationId - Authorization ID
   * @returns {Promise}
   */
  deleteAuthorization: async (authorizationId) => {
    try {
      const response = await api.delete(`/api/authorization/${authorizationId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting authorization:', error);
      throw error;
    }
  },

  /**
   * Bulk create authorizations (admin only)
   * @param {array} authorizationsArray - Array of authorization objects
   * @returns {Promise}
   */
  bulkCreateAuthorizations: async (authorizationsArray) => {
    try {
      const response = await api.post('/api/authorization/bulk-create', {
        authorizations: authorizationsArray
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk creating authorizations:', error);
      throw error;
    }
  }
};

export default authorizationService;
